<script>
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";

  import Tailwind from "./components/Tailwind.svelte";
  import AppToolbar from "./components/AppToolbar.svelte";
  import MultiSelectToolbar from "./components/MultiSelectToolbar.svelte";

  import PDFPage from "./components/PDFPage.svelte";

  import Image from "./components/objects/Image.svelte";
  import Text from "./components/objects/Text.svelte";
  import Drawing from "./components/objects/Drawing.svelte";
  import DrawingCanvas from "./components/objects/DrawingCanvas.svelte";
  import Shape from "./components/objects/Shape.svelte";

  import { editor } from "./stores/editor.js";
  import { MAX_OBJECT_DIMENSION } from "./lib/constants.js";

  let isMobile = false;

  onMount(() => {
    isMobile = window.innerWidth < 768;
    isSidebarOpen = !isMobile;
    editor.init();
    editor.loadTestPdf();

    const handleResize = () => {
      const currentlyMobile = window.innerWidth < 768;
      if (isMobile !== currentlyMobile) {
        isMobile = currentlyMobile;
        if (currentlyMobile && isSidebarOpen) isSidebarOpen = false;
        if (!currentlyMobile && !isSidebarOpen) isSidebarOpen = true;
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  let isSaveDialogOpen = false;
  let tempPdfFileName = "";
  let saveAsPng = false;
  let zipPngs = true;
  let exportRangeType = "all";
  let customExportRange = "";

  let isShiftPressed = false;
  let isMultiSelectMode = false;
  let isSidebarOpen = true;
  $: effectiveShiftPressed = isShiftPressed || isMultiSelectMode;

  $: hasGrouped = $editor.selectedElementIds.some((id) => {
    const pageElements = $editor.elements[$editor.selectedPageIndex];
    if (!pageElements) return false;
    const el = pageElements.find((e) => e.id === id);
    return el && el.groupId;
  });

  function scrollToPage(index) {
    editor.setActivePage(index);
    const element = document.getElementById(`page-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  function handleDrop(event) {
    const files = event.dataTransfer && event.dataTransfer.files;
    const file = files && files[0];
    editor.loadPdf(file);
  }

  function onPdfUpload(event) {
    editor.loadPdf(event.detail.file);
  }

  function onImageUpload(event) {
    editor.addImage({
      file: event.detail.file,
      compress: event.detail.compress,
      quality: event.detail.quality,
    });
  }

  function handleRename(event) {
    editor.setPdfFileName(event.detail.value);
  }

  function openSaveDialog() {
    tempPdfFileName = ($editor.pdfFileName || "document").replace(
      /\.(pdf|zip|png)$/i,
      "",
    );
    exportRangeType = "all";
    customExportRange = "";
    isSaveDialogOpen = true;
  }

  function parsePageRange(rangeStr, totalPages) {
    const pages = new Set();
    const parts = rangeStr.split(",").map((p) => p.trim());
    for (const part of parts) {
      if (!part) continue;
      if (part.includes("-")) {
        const [startStr, endStr] = part.split("-");
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);
        if (!isNaN(start) && !isNaN(end)) {
          for (
            let i = Math.max(1, start);
            i <= Math.min(totalPages, end);
            i++
          ) {
            pages.add(i - 1);
          }
        }
      } else {
        const num = parseInt(part, 10);
        if (!isNaN(num) && num >= 1 && num <= totalPages) {
          pages.add(num - 1);
        }
      }
    }
    return Array.from(pages).sort((a, b) => a - b);
  }

  function getPagesToExport() {
    const totalPages = $editor.pages.length;
    let pagesToExport = [];
    if (exportRangeType === "all") {
      pagesToExport = Array.from({ length: totalPages }, (_, i) => i);
    } else if (exportRangeType === "odd") {
      pagesToExport = Array.from({ length: totalPages }, (_, i) => i).filter(
        (i) => i % 2 === 0,
      );
    } else if (exportRangeType === "even") {
      pagesToExport = Array.from({ length: totalPages }, (_, i) => i).filter(
        (i) => i % 2 !== 0,
      );
    } else if (exportRangeType === "custom") {
      pagesToExport = parsePageRange(customExportRange, totalPages);
      if (pagesToExport.length === 0) {
        pagesToExport = Array.from({ length: totalPages }, (_, i) => i);
      }
    }
    return pagesToExport;
  }

  function confirmSave() {
    editor.setPdfFileName(tempPdfFileName);
    const pagesToExport = getPagesToExport();
    editor.save({ saveAsPng, zipPngs, pagesToExport });
    isSaveDialogOpen = false;
  }

  function handleAddText() {
    editor.addText();
  }

  function handleAddShape() {
    editor.addShape();
  }

  function handleAddDrawing() {
    if ($editor.currentPageIndex >= 0) editor.openDrawingCanvas();
  }

  function handleMeasure(scale, pageIndex) {
    editor.setPageScale(pageIndex, scale);
  }

  function finishDrawing(event) {
    const { originalWidth, originalHeight, svgPath } = event.detail;
    let scale = 1;
    if (originalWidth > MAX_OBJECT_DIMENSION) {
      scale = MAX_OBJECT_DIMENSION / originalWidth;
    }
    editor.addDrawing({ originalWidth, originalHeight, svgPath, scale });
    editor.closeDrawingCanvas();
  }

  function handleKeydown(event) {
    if (event.key === "Shift") isShiftPressed = true;
    if (
      event.target.isContentEditable ||
      ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName)
    ) {
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
      if (event.shiftKey) {
        editor.redo();
      } else {
        editor.undo();
      }
      event.preventDefault();
    } else if (
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === "y"
    ) {
      editor.redo();
      event.preventDefault();
    }
  }

  function handleKeyup(event) {
    if (event.key === "Shift") isShiftPressed = false;
  }

  function toggleMultiSelect() {
    isMultiSelectMode = !isMultiSelectMode;
    if (!isMultiSelectMode) {
      editor.clearSelection();
    }
  }

  function confirmMultiSelect() {
    isMultiSelectMode = false;
    editor.clearSelection();
  }
</script>

<svelte:window
  on:dragenter|preventDefault
  on:dragover|preventDefault
  on:drop|preventDefault={handleDrop}
  on:keydown={handleKeydown}
  on:keyup={handleKeyup}
/>

<Tailwind />

<AppToolbar
  {isMultiSelectMode}
  on:toggleMultiSelect={toggleMultiSelect}
  canEdit={$editor.currentPageIndex >= 0}
  canSave={$editor.pages.length > 0 &&
    !$editor.isSaving &&
    !!$editor.pdfSourceFile}
  isSaving={$editor.isSaving}
  on:uploadPdf={onPdfUpload}
  on:uploadImage={onImageUpload}
  on:rename={handleRename}
  on:save={openSaveDialog}
  on:addText={handleAddText}
  on:addShape={handleAddShape}
  on:addDrawing={handleAddDrawing}
  hasSelection={$editor.selectedElementIds.length > 0}
  on:bringToFront={() =>
    editor.bringToFront({ pageIndex: $editor.selectedPageIndex })}
  on:moveForward={() =>
    editor.moveForward({ pageIndex: $editor.selectedPageIndex })}
  on:moveBackward={() =>
    editor.moveBackward({ pageIndex: $editor.selectedPageIndex })}
  on:sendToBack={() =>
    editor.sendToBack({ pageIndex: $editor.selectedPageIndex })}
  on:clone={() => editor.cloneObject({ pageIndex: $editor.selectedPageIndex })}
  on:delete={() => {
    editor.removeObject({ pageIndex: $editor.selectedPageIndex });
    editor.clearSelection();
  }}
  canUndo={$editor.historyIndex > 0}
  canRedo={$editor.historyIndex < $editor.history.length - 1}
  on:undo={() => editor.undo()}
  on:redo={() => editor.redo()}
  pdfRenderScale={$editor.pdfRenderScale}
  zoomLevel={$editor.zoomLevel}
  hideObjectBorders={$editor.hideObjectBorders}
  imageCompressQuality={$editor.imageCompressQuality}
  on:changeRenderScale={(e) => editor.setPdfRenderScale(e.detail)}
  on:changeZoomLevel={(e) => editor.setZoomLevel(e.detail)}
  on:changeHideBorders={(e) => editor.setHideObjectBorders(e.detail)}
  on:changeKeepRatio={(e) => editor.setKeepImageRatio(e.detail)}
  on:changeCompressQuality={(e) => editor.setImageCompressQuality(e.detail)}
/>

<MultiSelectToolbar
  selectedCount={$editor.selectedElementIds.length}
  {hasGrouped}
  on:group={() => editor.groupSelected()}
  on:ungroup={() => editor.ungroupSelected()}
  on:align={(e) => editor.alignSelected(e.detail)}
/>

{#if isSaveDialogOpen}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
    style="background-color: rgba(15, 23, 42, 0.4);"
    transition:fly={{ y: -20, duration: 200 }}
  >
    <div
      class="bg-white rounded-2xl shadow-2xl p-4 md:p-6 w-full max-w-sm mx-4 border border-gray-100 max-h-[90vh] overflow-y-auto"
    >
      <h3 class="text-xl font-bold text-gray-800 mb-4 tracking-tight">
        Save your PDF
      </h3>
      <div class="mb-4">
        <label
          for="pdf-filename"
          class="block text-sm font-semibold text-gray-600 mb-2">檔案名稱</label
        >
        <div class="relative">
          <input
            id="pdf-filename"
            type="text"
            bind:value={tempPdfFileName}
            placeholder="document.pdf"
            class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all placeholder-gray-400 bg-gray-50 focus:bg-white text-gray-800 font-medium"
            on:keydown={(e) => e.key === "Enter" && confirmSave()}
            autofocus
          />
        </div>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-semibold text-gray-600 mb-2"
          >匯出頁面</label
        >
        <div class="grid grid-cols-2 gap-2 mb-2">
          <label
            class="flex items-center gap-2 cursor-pointer p-2 rounded-lg border {exportRangeType ===
            'all'
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
              : 'border-gray-200 hover:bg-gray-50 text-gray-700'} transition-all"
          >
            <input
              type="radio"
              bind:group={exportRangeType}
              value="all"
              class="hidden"
            />
            <span class="text-xs font-medium tracking-wide">全部頁面</span>
          </label>
          <label
            class="flex items-center gap-2 cursor-pointer p-2 rounded-lg border {exportRangeType ===
            'odd'
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
              : 'border-gray-200 hover:bg-gray-50 text-gray-700'} transition-all"
          >
            <input
              type="radio"
              bind:group={exportRangeType}
              value="odd"
              class="hidden"
            />
            <span class="text-xs font-medium tracking-wide">僅奇數頁</span>
          </label>
          <label
            class="flex items-center gap-2 cursor-pointer p-2 rounded-lg border {exportRangeType ===
            'even'
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
              : 'border-gray-200 hover:bg-gray-50 text-gray-700'} transition-all"
          >
            <input
              type="radio"
              bind:group={exportRangeType}
              value="even"
              class="hidden"
            />
            <span class="text-xs font-medium tracking-wide">僅雙數頁</span>
          </label>
          <label
            class="flex items-center gap-2 cursor-pointer p-2 rounded-lg border {exportRangeType ===
            'custom'
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
              : 'border-gray-200 hover:bg-gray-50 text-gray-700'} transition-all"
          >
            <input
              type="radio"
              bind:group={exportRangeType}
              value="custom"
              class="hidden"
            />
            <span class="text-xs font-medium tracking-wide">自定範圍</span>
          </label>
        </div>
        {#if exportRangeType === "custom"}
          <input
            type="text"
            bind:value={customExportRange}
            placeholder="例如: 1-5, 8, 11-13"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all placeholder-gray-400 text-sm"
          />
        {/if}
      </div>

      <div class="flex flex-col gap-2 mt-4 ml-1">
        <label
          class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700"
        >
          <input
            type="checkbox"
            bind:checked={saveAsPng}
            class="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
          />
          儲存為 PNG 圖片格式
        </label>

        {#if saveAsPng}
          <label
            class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 ml-6 mt-1 transition-all"
          >
            <input
              type="checkbox"
              bind:checked={zipPngs}
              class="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
            />
            將多個頁面打包成一個 Zip 壓縮檔
          </label>
        {/if}
      </div>

      <div class="flex justify-end gap-3 mt-8">
        <button
          class="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-xl transition-all btn-hover"
          on:click={() => (isSaveDialogOpen = false)}
        >
          取消
        </button>
        <button
          class="px-5 py-2.5 bg-indigo-600 text-white font-semibold hover:bg-indigo-700 rounded-xl shadow-glow transition-all btn-hover flex items-center gap-2"
          on:click={confirmSave}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            ><path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path><polyline points="17 21 17 13 7 13 7 21"
            ></polyline><polyline points="7 3 7 8 15 8"></polyline></svg
          >
          確定儲存
        </button>
      </div>
    </div>
  </div>
{/if}

<main class="min-h-screen pt-4 pb-24 md:pb-16 flex flex-col items-center">
  {#if $editor.isDrawingCanvasOpen}
    <div
      transition:fly={{ y: -200, duration: 300 }}
      class="fixed inset-x-0 z-40 bg-white border-b border-gray-200 shadow-lg"
      style="top: 56px; height: 50%;"
    >
      <DrawingCanvas
        on:finish={finishDrawing}
        on:cancel={editor.closeDrawingCanvas}
      />
    </div>
  {/if}

  {#if $editor.pages.length}
    <div class="flex w-full items-start relative">
      <aside
        class="flex-shrink-0 transition-all duration-300 bg-gray-50/90 md:bg-gray-50/80 backdrop-blur md:border-r border-gray-200 overflow-y-auto {isMobile
          ? 'absolute z-40 left-0 shadow-2xl h-full pb-32'
          : 'relative h-full'} {isSidebarOpen
          ? 'w-48 opacity-100'
          : 'w-0 opacity-0 overflow-hidden'}"
        style={isMobile
          ? "top: 0; scrollbar-width: none;"
          : "height: calc(100vh - 100px); position: sticky; top: 100px; scrollbar-width: none;"}
      >
        {#if isSidebarOpen}
          <div class="p-3 space-y-4">
            {#each $editor.pages as pagePromise, pageIndex (pagePromise)}
              <div
                class="cursor-pointer border-2 rounded-xl transition-all p-1.5 flex flex-col items-center gap-1 bg-white hover:shadow-md {pageIndex ===
                $editor.currentPageIndex
                  ? 'border-indigo-500 shadow-sm'
                  : 'border-transparent shadow-sm hover:border-indigo-200'}"
                on:click={() => scrollToPage(pageIndex)}
              >
                <span class="text-[10px] font-bold text-gray-400"
                  >PAGE {pageIndex + 1}</span
                >
                <div
                  class="pointer-events-none w-full flex justify-center bg-gray-50 overflow-hidden rounded shadow-inner"
                >
                  <PDFPage {pagePromise} zoomLevel={0.15} />
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </aside>

      <button
        on:click={() => (isSidebarOpen = !isSidebarOpen)}
        class="{isMobile
          ? 'fixed left-0 z-40 top-1/2 -translate-y-1/2'
          : 'sticky top-[120px] z-30'} flex items-center justify-center w-6 h-12 md:h-10 bg-white border border-gray-200 shadow-md md:shadow-sm rounded-r-lg md:rounded-r-xl hover:bg-gray-50 focus:outline-none transition-all group"
        style="margin-left: -1px;"
        title={isSidebarOpen ? "收合側邊欄" : "展開側邊欄"}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke-width="2.5"
          class="text-gray-400 group-hover:text-indigo-600 transition-transform {isSidebarOpen
            ? 'rotate-180'
            : ''}"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>

      <div
        class="flex-1 flex flex-col items-center py-4 md:py-8 overflow-y-auto w-full max-w-5xl mx-auto px-2 md:px-0"
        on:click|self={() => editor.clearSelection()}
      >
        <div class="flex flex-col items-center w-max min-w-full">
          {#each $editor.pages as pagePromise, pageIndex (pagePromise)}
            <div
              id="page-{pageIndex}"
              class="px-8 py-6 flex flex-col items-center mb-4"
              on:mousedown={() => editor.setActivePage(pageIndex)}
              on:touchstart={() => editor.setActivePage(pageIndex)}
            >
              <div
                class="relative shadow-soft transition-all duration-300 bg-white"
                class:shadow-glow={pageIndex === $editor.currentPageIndex}
              >
                <PDFPage
                  {pagePromise}
                  zoomLevel={$editor.zoomLevel}
                  on:measure={(e) => handleMeasure(e.detail.scale, pageIndex)}
                />

                <div
                  class="absolute top-0 left-0 transform origin-top-left"
                  style="transform: scale({$editor.pageScales[
                    pageIndex
                  ]}); touch-action: none;"
                >
                  {#each $editor.elements[pageIndex] as object (object.id)}
                    {#if object.type === "image"}
                      <Image
                        on:update={(e) =>
                          editor.patchObject({
                            pageIndex,
                            id: object.id,
                            updates: e.detail,
                          })}
                        on:select={() =>
                          editor.selectObject({
                            pageIndex,
                            id: object.id,
                            multi: effectiveShiftPressed,
                          })}
                        on:blur={() =>
                          !effectiveShiftPressed && editor.clearSelection()}
                        image={object.image}
                        x={object.x}
                        y={object.y}
                        width={object.width}
                        height={object.height}
                        keepRatio={object.keepRatio}
                        pageScale={$editor.pageScales[pageIndex]}
                        selected={$editor.selectedElementIds.includes(
                          object.id,
                        )}
                        showToolbar={$editor.selectedElementIds.length === 1}
                        hideBorders={$editor.hideObjectBorders}
                      />
                    {:else if object.type === "text"}
                      <Text
                        on:update={(e) =>
                          editor.patchObject({
                            pageIndex,
                            id: object.id,
                            updates: e.detail,
                          })}
                        on:draft={(e) =>
                          editor.updateObjectDraft({
                            pageIndex,
                            id: object.id,
                            updates: e.detail,
                          })}
                        on:select={() =>
                          editor.selectObject({
                            pageIndex,
                            id: object.id,
                            multi: effectiveShiftPressed,
                          })}
                        on:blur={() =>
                          !effectiveShiftPressed && editor.clearSelection()}
                        on:fontFamilyChange={(e) =>
                          editor.setActiveFontFamily(e.detail.fontFamily)}
                        html={object.html}
                        x={object.x}
                        y={object.y}
                        fontSize={object.fontSize}
                        lineHeight={object.lineHeight}
                        fontFamily={object.fontFamily}
                        color={object.color}
                        scaleX={object.scaleX}
                        scaleY={object.scaleY}
                        pageScale={$editor.pageScales[pageIndex]}
                        selected={$editor.selectedElementIds.includes(
                          object.id,
                        )}
                        showToolbar={$editor.selectedElementIds.length === 1}
                        hideBorders={$editor.hideObjectBorders}
                      />
                    {:else if object.type === "drawing"}
                      <Drawing
                        on:update={(e) =>
                          editor.patchObject({
                            pageIndex,
                            id: object.id,
                            updates: e.detail,
                          })}
                        on:select={() =>
                          editor.selectObject({
                            pageIndex,
                            id: object.id,
                            multi: effectiveShiftPressed,
                          })}
                        on:blur={() =>
                          !effectiveShiftPressed && editor.clearSelection()}
                        svgPath={object.svgPath}
                        x={object.x}
                        y={object.y}
                        width={object.width}
                        originalWidth={object.originalWidth}
                        originalHeight={object.originalHeight}
                        pageScale={$editor.pageScales[pageIndex]}
                        selected={$editor.selectedElementIds.includes(
                          object.id,
                        )}
                        showToolbar={$editor.selectedElementIds.length === 1}
                        hideBorders={$editor.hideObjectBorders}
                      />
                    {:else if object.type === "shape"}
                      <Shape
                        on:update={(e) =>
                          editor.patchObject({
                            pageIndex,
                            id: object.id,
                            updates: e.detail,
                          })}
                        on:select={() =>
                          editor.selectObject({
                            pageIndex,
                            id: object.id,
                            multi: effectiveShiftPressed,
                          })}
                        on:blur={() =>
                          !effectiveShiftPressed && editor.clearSelection()}
                        x={object.x}
                        y={object.y}
                        width={object.width}
                        height={object.height}
                        shapeType={object.shapeType}
                        keepRatio={object.keepRatio}
                        fillColor={object.fillColor}
                        strokeColor={object.strokeColor}
                        strokeWidth={object.strokeWidth}
                        borderRadius={object.borderRadius}
                        pageScale={$editor.pageScales[pageIndex]}
                        selected={$editor.selectedElementIds.includes(
                          object.id,
                        )}
                        showToolbar={$editor.selectedElementIds.length === 1}
                        hideBorders={$editor.hideObjectBorders}
                      />
                    {/if}
                  {/each}
                </div>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {:else}
    <div
      class="px-4 flex items-center justify-center mt-12 mb-12"
      style="min-h: 60vh;"
    >
      <div class="max-w-xl w-full mx-auto">
        <div
          class="bg-white/80 backdrop-blur-sm border-2 border-dashed border-indigo-200 rounded-3xl p-8 md:p-12 text-center shadow-soft transition-all hover:shadow-lg hover:border-indigo-400 bg-white/50 flex flex-col items-center"
        >
          <div
            class="w-16 h-16 md:w-20 md:h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-6 shadow-sm cursor-pointer btn-hover transition-colors hover:bg-indigo-100"
            on:click={() =>
              document.querySelector('input[accept="application/pdf"]').click()}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              class="text-indigo-600"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              ></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
          </div>
          <div
            class="text-gray-800 font-bold text-xl md:text-2xl tracking-tight mb-2"
          >
            Drop a PDF here
          </div>
          <div class="text-gray-500 font-medium text-sm md:text-base">
            or click the icon above to choose a file
          </div>
        </div>
      </div>
    </div>
  {/if}
</main>
